/**
 * Place parameter dialogs.
 *
 * The manual's Place commands open a parameter dialog before you draw: Place
 * Line asks for layer and thickness, Place Pad for type and size, and so on
 * (Chapter 3). You set the parameters, Confirm, and the status line then reads
 * "Place: Line" for as many items as you care to lay.
 *
 * These dialogs edit the document's own DefaultLine::/DefaultPad::/etc. records
 * — the same values the status-bar pickers change — so the parameters persist
 * with the file, and Confirm simply enters the drawing tool.
 */

import {
  type CadDocument, type PadType, type TextAnchor,
  PAD_TYPE_LABELS, padIsThrough,
} from '@sircad/core/model.ts';
import { fromDisplay, toDisplay, type DisplayUnit } from '@sircad/core/units.ts';
import { openDialog } from './dialog.ts';

export type PlaceTarget = 'line' | 'arc' | 'pad' | 'text';

export interface PlaceParamsOptions {
  doc: CadDocument;
  units: DisplayUnit;
  /** True for a schematic wire, so the dialog titles itself accordingly. */
  wire?: boolean;
  /** Called when the user confirms; the caller then enters the tool. */
  onConfirm(): void;
}

const ANCHORS: TextAnchor[] = ['BL', 'BC', 'BR', 'ML', 'MC', 'MR', 'TL', 'TC', 'TR'];
const PAD_TYPES: PadType[] = ['T0', 'T1', 'T2', 'T3', 'S0', 'S1', 'S2', 'S3'];

export function showPlaceParams(target: PlaceTarget, opts: PlaceParamsOptions): void {
  const title = target === 'line'
    ? (opts.wire ? 'Place Signal Wire' : 'Place Line')
    : `Place ${target[0]!.toUpperCase()}${target.slice(1)}`;

  openDialog({
    title,
    build(body, handle) {
      const form = document.createElement('form');
      form.className = 'modal-form';
      body.append(form);

      const rows = buildRows(target, opts);
      for (const r of rows) form.append(r);

      const bar = document.createElement('div');
      bar.className = 'modal-buttons';

      // Confirm runs its action then closes; Cancel just closes. A guard stops
      // the action firing twice when Enter both submits and clicks Confirm.
      let done = false;
      const doConfirm = (): void => {
        if (done) return;
        done = true;
        handle.close();
        opts.onConfirm();
      };

      const cancel = document.createElement('button');
      cancel.type = 'button';
      cancel.className = 'btn';
      cancel.textContent = 'Cancel';
      cancel.addEventListener('click', () => handle.close());
      const confirm = document.createElement('button');
      confirm.type = 'submit';
      confirm.className = 'btn primary';
      confirm.textContent = 'Confirm';
      bar.append(cancel, confirm);
      form.append(bar);

      handle.setAccept(doConfirm); // clicking away accepts, as the original does
      confirm.addEventListener('click', (ev) => { ev.preventDefault(); doConfirm(); });
      form.addEventListener('submit', (ev) => { ev.preventDefault(); doConfirm(); });

      queueMicrotask(() => form.querySelector<HTMLElement>('input,select')?.focus());
    },
  });
}

// ---------------------------------------------------------------------------

function buildRows(target: PlaceTarget, opts: PlaceParamsOptions): HTMLElement[] {
  const d = opts.doc;
  switch (target) {
    case 'line':
      // A schematic wire has nothing to set. It goes on the signals layer at
      // ten mil whatever this dialog says, so asking would be a control that
      // does nothing — say what will happen instead. A line, and a trace on a
      // board, are the commands that take a layer and a width.
      if (opts.wire && d.kind === 'sch') return [fixedNote()];
      return [
        layerRow(opts, () => d.defaults.line?.layer, (v) => setLineLayer(d, v)),
        lengthRow('Thickness', opts,
          () => d.defaults.line?.width ?? 0.01,
          (v) => { (d.defaults.line ??= { width: v, style: 0 }).width = v; }),
      ];
    case 'arc':
      return [
        layerRow(opts, () => d.defaults.arc?.layer, (v) => { d.defaults.arc = { layer: v }; }),
        lengthRow('Pen width', opts,
          () => d.defaults.line?.width ?? 0.01,
          (v) => { (d.defaults.line ??= { width: v, style: 0 }).width = v; }),
      ];
    case 'pad':
      return [
        padTypeRow(opts),
        lengthRow('X size', opts,
          () => d.defaults.pad?.size ?? 0.06,
          (v) => { (d.defaults.pad ??= { type: 'T1', size: v }).size = v; }),
        lengthRow('Drill', opts,
          () => d.defaults.pad?.hole ?? 0,
          (v) => { if (d.defaults.pad && padIsThrough(d.defaults.pad.type)) d.defaults.pad.hole = v; }),
      ];
    case 'text':
      return [
        layerRow(opts, () => d.defaults.text?.layer, (v) => { (d.defaults.text ??= { height: 0.06 }).layer = v; }),
        lengthRow('Height', opts,
          () => d.defaults.text?.height ?? 0.06,
          (v) => { (d.defaults.text ??= { height: v }).height = v; }),
        anchorRow(opts),
      ];
  }
}

function setLineLayer(d: CadDocument, v: number): void {
  (d.defaults.line ??= { width: 0.01, style: 0 }).layer = v;
}

function labelled(label: string, control: HTMLElement): HTMLElement {
  const wrap = document.createElement('label');
  wrap.className = 'field';
  const span = document.createElement('span');
  span.textContent = label;
  wrap.append(span, control);
  return wrap;
}

/**
 * A layer dropdown. The stored default may be a name, a number, or unset; the
 * status bar's active layer is a better guide, but the dialog edits the
 * default so it persists — so we resolve whatever is there to an index.
 */
function layerRow(
  opts: PlaceParamsOptions,
  get: () => number | string | undefined,
  set: (v: number) => void,
): HTMLElement {
  const sel = document.createElement('select');
  opts.doc.layers.forEach((l, i) => {
    const o = document.createElement('option');
    o.value = String(i);
    o.textContent = `${i}  ${l.name}`;
    sel.append(o);
  });
  const cur = get();
  const idx = typeof cur === 'number'
    ? cur
    : typeof cur === 'string'
      ? Math.max(0, opts.doc.layers.findIndex((l) => l.name === cur))
      : 0;
  sel.value = String(idx);
  sel.addEventListener('change', () => set(Number(sel.value)));
  set(Number(sel.value)); // persist the shown default even if unchanged
  return labelled('Layer', sel);
}

function lengthRow(
  label: string, opts: PlaceParamsOptions,
  get: () => number, set: (v: number) => void,
): HTMLElement {
  const input = document.createElement('input');
  input.type = 'number';
  // No step — see the note in edit.ts. Four decimals are shown against a step
  // of three, so this box could arrive already invalid, and Confirm is a
  // submit button.
  input.step = 'any';
  input.min = '0';
  input.value = String(Number(toDisplay(get(), opts.units).toFixed(4)));
  input.addEventListener('input', () => {
    const v = parseFloat(input.value);
    if (Number.isFinite(v) && v >= 0) set(fromDisplay(v, opts.units));
  });
  return labelled(`${label} (${opts.units})`, input);
}

function padTypeRow(opts: PlaceParamsOptions): HTMLElement {
  const sel = document.createElement('select');
  for (const t of PAD_TYPES) {
    const o = document.createElement('option');
    o.value = t;
    o.textContent = `${t} — ${PAD_TYPE_LABELS[t]}`;
    sel.append(o);
  }
  sel.value = opts.doc.defaults.pad?.type ?? 'T1';
  sel.addEventListener('change', () => {
    (opts.doc.defaults.pad ??= { type: 'T1', size: 0.06 }).type = sel.value as PadType;
  });
  return labelled('Pad type', sel);
}

function anchorRow(opts: PlaceParamsOptions): HTMLElement {
  const sel = document.createElement('select');
  for (const a of ANCHORS) {
    const o = document.createElement('option');
    o.value = a;
    o.textContent = a;
    sel.append(o);
  }
  // Text default has no anchor field; default to bottom-left as the tool does.
  sel.value = 'BL';
  return labelled('Anchor', sel);
}

/** What a schematic wire will be, since none of it is up for discussion. */
function fixedNote(): HTMLElement {
  const p = document.createElement('p');
  p.className = 'modal-hint';
  p.textContent = 'Signals layer, 10 mil — a schematic wire takes no parameters.';
  return p;
}
